import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { ApiError } from '../api/client'

// Wraps any view backed by a query, so loading and failure look the same
// everywhere. Same contract as mik-ng's RemoteContent.
//
// Children stay mounted while loading, with the spinner over them: a list
// that unmounts and remounts on every refetch loses scroll position, which
// on a phone means losing your place in the day's bookings.
export function RemoteContent({
  isLoading,
  error,
  children,
  minHeight = 160,
}: {
  isLoading?: boolean
  error?: unknown
  children: ReactNode
  minHeight?: number
}) {
  if (error) {
    return (
      <Alert severity={statusSeverity(error)} sx={{ my: 2 }}>
        {messageFor(error)}
      </Alert>
    )
  }

  return (
    <Box sx={{ position: 'relative', minHeight: isLoading ? minHeight : undefined }}>
      {children}
      {isLoading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            opacity: 0.6,
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
    </Box>
  )
}

function statusSeverity(error: unknown) {
  // A 403 is not a fault, it is a boundary -- say so in a gentler colour.
  return error instanceof ApiError && error.status === 403 ? 'warning' : 'error'
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong'
}
